import React, { useState, useEffect } from "react";
import { auth, db } from "./firebaseConfig";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  arrayUnion 
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./joinTeacher.css";

const JoinTeacher = ({ user }) => {
  const navigate = useNavigate();
  const [teacherCode, setTeacherCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [darkMode, setDarkMode] = useState(() => {
    // Get dark mode preference from localStorage or default to false
    const savedMode = localStorage.getItem("darkMode");
    return savedMode ? JSON.parse(savedMode) : false;
  });

  // Check if user is logged in
  useEffect(() => {
    if (!user) {
      navigate("/");
    }
  }, [user, navigate]);

  // Handle teacher code input change
  const handleTeacherCodeChange = (e) => {
    setTeacherCode(e.target.value);
    // Clear any previous messages when user starts typing
    if (message.text) {
      setMessage({ text: "", type: "" });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate input
    if (!teacherCode.trim()) {
      setMessage({ 
        text: "Please enter a teacher code", 
        type: "error" 
      });
      return;
    }

    setIsLoading(true);
    setMessage({ text: "", type: "" });

    try {
      // Query the tenants collection to validate the teacher code
      const tenantsRef = collection(db, "tenants");
      const q = query(tenantsRef, where("teacherCode", "==", teacherCode.trim()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // No matching teacher code found
        setMessage({ 
          text: "Teacher code is invalid.", 
          type: "error" 
        });
        setIsLoading(false);
        return;
      }

      // Get the teacher's document
      const teacherDoc = querySnapshot.docs[0];
      const teacherData = teacherDoc.data();
      const teacherName = teacherData.teacherName || "Unknown";

      // Update the student's record
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        tenant: teacherDoc.id,
        teacherCodes: arrayUnion(teacherCode.trim()),
        assignedBy: teacherName
      });

      // Show success message
      setMessage({ 
        text: `Successfully joined ${teacherName}'s class! Redirecting...`, 
        type: "success" 
      });

      // Redirect back to dashboard after a delay
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error) {
      console.error("Error joining teacher:", error);
      setMessage({ 
        text: "An error occurred. Please try again.", 
        type: "error" 
      });
      setIsLoading(false);
    }
  };

  return (
    <div className={`join-teacher-container ${darkMode ? 'dark-mode' : ''}`} 
         style={{backgroundImage: darkMode ? "none" : "url('/green1.jpg')"}}>
      <div className="join-teacher-card">
        <div className="join-teacher-header">
          <h1 className="join-teacher-title">Join Teacher</h1>
          <p className="join-teacher-subtitle">
            Enter the teacher code to join their class
          </p>
        </div>
        <form className="join-teacher-form" onSubmit={handleSubmit}>
          <div className="input-group">
            <label className="input-label" htmlFor="teacherCode">
              Teacher Code
            </label>
            <input
              id="teacherCode"
              type="text"
              className="input-field"
              value={teacherCode}
              onChange={handleTeacherCodeChange}
              placeholder="Enter teacher code (e.g., AI-TEACHER-123)"
              disabled={isLoading}
              autoComplete="off"
            />
          </div>
          
          {message.text && (
            <div className={`message ${message.type === "error" ? "error-message" : "success-message"}`}>
              {message.text}
            </div>
          )}
          
          <button
            type="submit"
            className="submit-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Validating...
              </>
            ) : (
              "Join Class"
            )}
          </button>
          
          <div 
            className="back-link"
            onClick={() => navigate("/dashboard")}
          >
            ← Back to Dashboard
          </div>
        </form>
      </div>
    </div>
  );
};

export default JoinTeacher;
